import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';

const Banner = styled.div`
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  padding: 10px 16px;
  margin-bottom: 14px;
  font-family: Lato, sans-serif;
  font-size: 13px;
  color: #555;
  position: relative;
  z-index: 1000;
`;

const BannerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BannerLabel = styled.span`
  flex: 1;
  font-weight: 600;
`;

const NotesRow = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const NotesArea = styled.textarea`
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 12px;
  font-family: Lato, sans-serif;
  resize: vertical;
  min-height: 56px;
  color: #333;
`;

const RateButton = styled.button<{ color: 'green' | 'red' | 'default' }>`
  border: 0;
  border-radius: 4px;
  background: ${p => p.color === 'green' ? '#21ba45' : p.color === 'red' ? '#db2828' : '#999'};
  color: white;
  padding: 6px 14px;
  font-weight: bold;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  &:disabled { opacity: 0.55; cursor: default; }
`;

const DismissButton = styled.button`
  background: none;
  border: 0;
  color: #aaa;
  font-size: 18px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
`;

const SESSION_KEY = 'taxExtractionFeedbackId';

const ExtractionRatingBanner = ({ propertyId }: { propertyId?: string | number }) => {
  const [feedbackId, setFeedbackId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [enteringNotes, setEnteringNotes] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (propertyId != null && String(parsed.propertyId) !== String(propertyId)) return;
      if (parsed.feedbackId) setFeedbackId(Number(parsed.feedbackId));
    } catch {
      if (stored) setFeedbackId(Number(stored));
    }
  }, [propertyId]);

  const dismiss = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setFeedbackId(null);
    setEnteringNotes(false);
    setNotes('');
  };

  const rate = async (reviewStatus: 'Match' | 'Mismatch', notesText?: string) => {
    if (!feedbackId) return;
    setSaving(true);
    try {
      await axios.post('/api/orders/SetTaxExtractionFeedbackReview', {
        id: feedbackId,
        reviewStatus,
        notes: notesText?.trim() || null,
      });
      sessionStorage.removeItem(SESSION_KEY);
      setDone(true);
      setTimeout(() => setFeedbackId(null), 1500);
    } catch {
      setSaving(false);
    }
  };

  if (!feedbackId) return null;

  return (
    <Banner>
      <BannerRow>
        <BannerLabel>
          {done ? '✓ Rating saved — thank you!' : 'Was the auto-fill accurate?'}
        </BannerLabel>
        {!done && !enteringNotes && (
          <>
            <RateButton color="green" disabled={saving} onClick={() => rate('Match')}>
              Yes, looks good
            </RateButton>
            <RateButton color="red" disabled={saving} onClick={() => setEnteringNotes(true)}>
              No, had issues
            </RateButton>
          </>
        )}
        <DismissButton onClick={dismiss} title="Dismiss">×</DismissButton>
      </BannerRow>

      {!done && enteringNotes && (
        <NotesRow>
          <NotesArea
            placeholder="What was wrong? (e.g. wrong year, missing installment, wrong amount) — optional"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            autoFocus
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <RateButton color="red" disabled={saving} onClick={() => rate('Mismatch', notes)}>
              Submit
            </RateButton>
            <RateButton color="default" disabled={saving} onClick={() => setEnteringNotes(false)}>
              Cancel
            </RateButton>
          </div>
        </NotesRow>
      )}
    </Banner>
  );
};

export default ExtractionRatingBanner;
